import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { perfMark, perfRecord, logPlayResult } from "@/lib/perfLog";
import { resolveSceneFit } from "@/lib/sceneFit";
import { captureVideoFrame, releaseCanvasFrame } from "@/lib/videoFrame";
import { afterPaint, afterVideoFrame } from "@/lib/mediaPaint";
import { HANDOFF_FADE_MS } from "../lib/mediaPresentation";

// Displays a pre-captured (possibly CORS-tainted) canvas holding the exact
// last frame of the outgoing scene. Tainted canvases can't be read back but
// render fine, which makes the crossfade pixel-perfect regardless of CORS.
function FrozenFrame({ canvas, className, fit, style }) {
  const hostRef = useRef(null);
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !canvas) return;
    canvas.style.position = "absolute";
    canvas.style.inset = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.objectFit = fit === "cover" ? "cover" : "contain";
    host.appendChild(canvas);
    return () => { try { host.removeChild(canvas); } catch { /* ignore */ } };
  }, [canvas, fit]);
  return <div ref={hostRef} className={className} style={style} data-testid="scene-media-frozen-frame" />;
}

/**
 * SceneMedia — renders the foreground scene at `object-contain` so the full
 * media is always visible. The letterbox bars are filled by the sibling
 * `LetterboxBackdrop`. Admins can opt into `object-cover` per scene via the
 * scene's `display_mode` field.
 *
 * Video playback notes (iter22+):
 *  - We treat the `poster` attribute as a *loading placeholder* only — it
 *    NEVER permanently replaces the foreground video. Only a definitive
 *    unsupported source, decoder, or network error (codes 4, 3, and 2)
 *    after a real load attempt switches us to the static-poster fallback.
 *  - We call `.play()` explicitly on mount AND on `canplay`. Desktop Chrome
 *    occasionally honours `autoplay` but doesn't actually start the stream
 *    until a programmatic `.play()` lands — without it, the poster stays
 *    visible forever.
 *  - Every `.play()` rejection is captured and forwarded to perfLog so a
 *    silent autoplay denial is never possible — the ViewerDebugPanel
 *    surfaces the rejection name + message.
 *  - The `onStatus` callback emits the live `<video>` element state for an
 *    admin debug panel; public users never see it.
 */
export default function SceneMedia({ scene, onLoadedMedia, onReady, onDisplayed, onDisplayFailed, fadingOut, holdOutgoing = false, onStatus, project, frozenFrame, instant, active = true }) {
  const videoRef = useRef(null);
  const activeRef = useRef(active);
  const disposedRef = useRef(false);
  activeRef.current = active;
  const [hardFailed, setHardFailed] = useState(false);
  // When mounted as the incoming half of a crossfade (`instant`), skip the
  // fade-in animation: the frozen outgoing frame fades out ON TOP of us, so
  // we must be fully opaque from the first paint (else the dark backdrop
  // bleeds through mid-crossfade). Captured once at mount so the class never
  // flips when the transition state clears.
  const skipFadeInRef = useRef(!!instant);
  // iter48 — a video is only revealed once it is actually PLAYING. Until then
  // we show its poster underneath and keep the <video> at opacity:0. iOS draws
  // its native grey Play button on any paused/loading inline video (and it
  // cannot be hidden via CSS in a standalone PWA); keeping the element
  // invisible until a playing frame is submitted means that button is never visible. Scenes
  // with no poster remain covered by the outgoing frame during a handoff.
  const [revealed, setRevealed] = useState(false);
  const pausedFrameRef = useRef(null);
  const [pausedFrame, setPausedFrame] = useState(null);
  const pausedPresentationRef = useRef(null);
  const videoPresentationRef = useRef(0);
  const cancelVideoFrameRef = useRef(null);
  const cancelNativePaintRef = useRef(null);
  const cancelCanvasPaintRef = useRef(null);
  const cancelImagePaintRef = useRef(null);
  const imagePresentationRef = useRef(0);
  const onDisplayedRef = useRef(onDisplayed);
  onDisplayedRef.current = onDisplayed;
  const capturedFrameRef = useRef(false);
  const decodedFrameDisplayedRef = useRef(false);
  const isVideo = scene.type === "loop_video" || scene.type === "scrubbable" || scene.type === "transition";
  const effectiveFit = resolveSceneFit(scene, project);
  const fit = effectiveFit === "cover" ? "object-cover" : "object-contain";
  const fitCls = `absolute inset-0 w-full h-full ${fit}`;
  const cls = `${fitCls} ${fadingOut ? (holdOutgoing ? "" : "fade-out") : (skipFadeInRef.current ? "" : "fade-in")}`;
  const outgoingStyle = fadingOut ? { animationDuration: `${HANDOFF_FADE_MS}ms` } : undefined;
  const videoRevealed = active && revealed;

  const cancelVideoPresentation = useCallback(() => {
    videoPresentationRef.current += 1;
    cancelVideoFrameRef.current?.();
    cancelVideoFrameRef.current = null;
    cancelNativePaintRef.current?.();
    cancelNativePaintRef.current = null;
  }, []);

  // Safari may draw its native Play badge whenever a visible video pauses.
  // Keep the last decoded frame above the poster while playback resumes.
  // Drawing a cross-origin video is safe here: the canvas is never read back.
  const holdPausedFrame = useCallback((video) => {
    cancelVideoPresentation();
    cancelCanvasPaintRef.current?.();
    cancelCanvasPaintRef.current = null;
    const frame = captureVideoFrame(video, pausedFrameRef.current);
    if (frame) {
      pausedFrameRef.current = frame;
      capturedFrameRef.current = true;
      // A fresh presentation token also invalidates a queued callback when
      // the same canvas is reused for a later pause.
      const presentation = { canvas: frame };
      pausedPresentationRef.current = presentation;
      setPausedFrame(presentation);
    } else if (pausedFrameRef.current?.width === 0) {
      pausedPresentationRef.current = null;
      setPausedFrame(null);
    }
    setRevealed(false);
    return !!frame;
  }, [cancelVideoPresentation]);

  // A decoded canvas is useful even when autoplay is denied, but it is not a
  // displayed frame until FrozenFrame has attached it and the browser painted.
  useLayoutEffect(() => {
    if (!active || fadingOut || !pausedFrame || videoRevealed) return undefined;
    const cancel = afterPaint(() => {
      if (disposedRef.current || !activeRef.current || pausedPresentationRef.current !== pausedFrame || !pausedFrame.canvas.isConnected) return;
      onDisplayedRef.current?.();
    });
    cancelCanvasPaintRef.current = cancel;
    return cancel;
  }, [active, fadingOut, pausedFrame, videoRevealed]);

  // Keep the protective canvas ABOVE the newly revealed native layer until
  // that layer has had a paint opportunity. Releasing it in the reveal commit
  // exposes the previous/poster surface for one compositor frame on iOS.
  useLayoutEffect(() => {
    if (!videoRevealed) return undefined;
    const video = videoRef.current;
    const presentation = videoPresentationRef.current;
    const cancel = afterPaint(() => {
      if (disposedRef.current || !activeRef.current || videoPresentationRef.current !== presentation || videoRef.current !== video || !video?.isConnected || video.paused) return;
      onDisplayedRef.current?.();
      releaseCanvasFrame(pausedFrameRef.current);
      pausedPresentationRef.current = null;
      setPausedFrame(null);
    });
    cancelNativePaintRef.current = cancel;
    return cancel;
  }, [videoRevealed]);

  // A decoded first frame is already safe to show even if play() is still
  // pending. Capture it once; the paused native video stays hidden, and the
  // outgoing scene no longer has to wait for a playing event to hand off.
  const presentDecodedFrame = (video) => {
    if (decodedFrameDisplayedRef.current || !activeRef.current || disposedRef.current) return;
    if (holdPausedFrame(video)) {
      decodedFrameDisplayedRef.current = true;
    }
  };

  // Capture before the browser paints the now-hidden video. A passive effect
  // would briefly expose its poster between the video and frozen frame.
  useLayoutEffect(() => {
    if (active || fadingOut) return;
    const video = videoRef.current;
    if (!video) return;
    holdPausedFrame(video);
    try { if (!video.paused) video.pause(); } catch (_) { /* ignore */ }
  }, [active, fadingOut, holdPausedFrame]);

  // Reset hard-failure when the scene changes, otherwise the wrong scene's
  // failure could "stick" if the parent re-uses the same component instance.
  useEffect(() => { setHardFailed(false); }, [scene?.id]);

  // iter47 — only ever report a *real* intrinsic size. Reporting the 1920×1080
  // fallback while videoWidth is still 0 (which happens on iOS after the
  // WebMediaPlayer decoder is under pressure from rapid A→B→A slides) maps the
  // zone polygons against the wrong aspect ratio → the zones "detach" from the
  // scene. The parent keeps zones hidden until we lock a correct size, so a
  // slightly-late report is always safer than a wrong one.
  const reportDims = (v) => {
    if (v && v.videoWidth > 0 && v.videoHeight > 0) onLoadedMedia?.(v.videoWidth, v.videoHeight);
  };

  // load may precede completion of image decoding on mobile. Announce only
  // pixels that can be displayed, and ignore a decode finishing after removal.
  const displayLoadedImage = (image) => {
    const presentation = ++imagePresentationRef.current;
    cancelImagePaintRef.current?.();
    const display = () => {
      if (disposedRef.current || !image.isConnected || imagePresentationRef.current !== presentation) return;
      if (image.naturalWidth > 0 && image.naturalHeight > 0) onLoadedMedia?.(image.naturalWidth, image.naturalHeight);
      onReady?.();
      cancelImagePaintRef.current = afterPaint(() => {
        // Static pixels remain displayable while a panel covers the scene;
        // no new load event is guaranteed when that panel closes.
        if (!disposedRef.current && image.isConnected && imagePresentationRef.current === presentation) onDisplayedRef.current?.();
      });
    };
    if (typeof image.decode !== "function") { display(); return; }
    image.decode().then(display, () => {
      if (!disposedRef.current && image.isConnected && imagePresentationRef.current === presentation) onDisplayFailed?.();
    });
  };

  // iter47 — iOS (especially standalone PWA) is slow to reclaim the
  // WebMediaPlayer/decoder slot held by an unmounted <video>. After 4-5 rapid
  // scene slides the slots exhaust: the incoming video stalls (its native grey
  // Play button appears and cannot be hidden by CSS because it is drawn by the
  // OS media stack, not the web shadow DOM) and reports 0×0 dimensions. We
  // explicitly tear the element down on unmount to release the slot right away.
  useEffect(() => {
    const v = videoRef.current;
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      cancelVideoPresentation();
      cancelCanvasPaintRef.current?.();
      cancelImagePaintRef.current?.();
      imagePresentationRef.current += 1;
      pausedPresentationRef.current = null;
      releaseCanvasFrame(pausedFrameRef.current);
      pausedFrameRef.current = null;
      if (!v) return;
      try {
        v.pause();
        v.removeAttribute("src");
        v.load(); // forces the decoder/WebMediaPlayer slot to be released now
      } catch { /* ignore */ }
    };
  }, [cancelVideoPresentation]);

  // Explicit play loop — Desktop Chrome AND iOS Safari occasionally sit on
  // the poster despite autoplay/muted being set. We call `.play()` on mount
  // and on every relevant readiness event, and log the precise rejection
  // reason so silent denials cannot happen.
  useEffect(() => {
    if (!isVideo || hardFailed) return;
    const v = videoRef.current;
    if (!v) return;
    if (!active) {
      // The layout effect already froze and paused this covered scene.
      return;
    }

    const tMount = performance.now();
    perfMark(`scene:${scene?.id}:mount`);
    let canplayLogged = false;

    const tryPlay = (origin) => {
      if (!activeRef.current || disposedRef.current) return;
      const p = v.play?.();
      if (!p?.then) {
        logPlayResult(`scene:${scene?.id}:${origin}`, !v.paused, null);
        return;
      }
      p.then(() => {
        // A play promise may settle after a panel opened or the scene was
        // removed. Do not let that pending attempt restart hidden media.
        if (!activeRef.current || disposedRef.current) {
          try { v.pause(); } catch (_) { /* ignore */ }
          return;
        }
        logPlayResult(`scene:${scene?.id}:${origin}`, true, null);
      }).catch((err) => {
        logPlayResult(`scene:${scene?.id}:${origin}`, false, err);
      });
    };

    // Attempt immediately — readyState may already be HAVE_FUTURE_DATA on
    // a cached re-mount (e.g. when the user navigates A→B→A).
    tryPlay("mount");

    const onLoadStart    = () => { /* noop, kept for event coverage in status */ };
    const onLoadedMeta   = () => { reportDims(v); tryPlay("loadedmetadata"); };
    const onLoadedData   = () => { reportDims(v); };
    const onCanPlay      = () => {
      if (!canplayLogged) {
        canplayLogged = true;
        perfRecord(`scene:${scene?.id}:canplay`, performance.now() - tMount, {
          videoWidth: v.videoWidth, videoHeight: v.videoHeight,
          readyState: v.readyState,
        });
      }
      reportDims(v);
      tryPlay("canplay");
    };
    const onWaiting = () => {
      perfRecord(`scene:${scene?.id}:waiting`, performance.now() - tMount, {
        readyState: v.readyState, networkState: v.networkState,
      });
    };
    const onStalled = () => {
      perfRecord(`scene:${scene?.id}:stalled`, performance.now() - tMount, {
        readyState: v.readyState, networkState: v.networkState,
      });
    };
    const onPause = () => {
      // Loop videos should never pause on their own. Re-attempt play() —
      // logging the result so we can see why if it keeps re-pausing.
      if (scene?.type === "loop_video" && !v.ended) tryPlay("auto-pause-recover");
    };

    v.addEventListener("loadstart", onLoadStart);
    v.addEventListener("loadedmetadata", onLoadedMeta);
    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("stalled", onStalled);
    v.addEventListener("pause", onPause);

    return () => {
      v.removeEventListener("loadstart", onLoadStart);
      v.removeEventListener("loadedmetadata", onLoadedMeta);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("stalled", onStalled);
      v.removeEventListener("pause", onPause);
    };
  }, [isVideo, hardFailed, scene?.media_url, scene?.id, scene?.type, active, holdPausedFrame]);

  // Live status emitter for the admin debug panel. Includes networkState
  // and an `everPlayed` flag so the debug HUD can flag a stuck-paused state.
  useEffect(() => {
    if (!isVideo || !onStatus) return;
    const v = videoRef.current;
    if (!v) return;
    let everPlayed = false;
    const emit = (extra = {}) => {
      const err = v.error ? { code: v.error.code, message: v.error.message || "" } : null;
      if (!v.paused) everPlayed = true;
      onStatus({
        readyState: v.readyState,
        networkState: v.networkState,
        loadedMetadata: v.readyState >= 1,
        canPlay: v.readyState >= 3,
        paused: v.paused,
        ended: v.ended,
        everPlayed,
        currentTime: v.currentTime,
        duration: v.duration,
        videoWidth: v.videoWidth,
        videoHeight: v.videoHeight,
        error: err,
        ...extra,
      });
    };
    const events = ["loadedmetadata", "canplay", "playing", "pause", "timeupdate", "error", "stalled", "waiting", "ended"];
    events.forEach(e => v.addEventListener(e, emit));
    emit();
    return () => events.forEach(e => v.removeEventListener(e, emit));
  }, [isVideo, onStatus, scene?.media_url]);

  // iter28: rotation/resize defence. iOS Safari occasionally pauses a
  // <video> across an orientation change AND surfaces its own native
  // grey play button on the poster. We listen for orientation+resize and
  // re-attempt play() so the loop keeps running.
  useEffect(() => {
    if (!isVideo || hardFailed || !active) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    let timer = null;
    const onMaybeRotated = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (!activeRef.current || disposedRef.current || !v.paused) return;
        const p = v.play?.();
        if (p?.then) p.then(
          () => {
            if (!activeRef.current || disposedRef.current) {
              try { v.pause(); } catch (_) { /* ignore */ }
              return;
            }
            logPlayResult(`scene:${scene?.id}:rotation-recover`, true, null);
          },
          (err) => logPlayResult(`scene:${scene?.id}:rotation-recover`, false, err),
        );
      }, 350);
    };
    window.addEventListener("orientationchange", onMaybeRotated, true);
    window.addEventListener("resize", onMaybeRotated, true);
    document.addEventListener("visibilitychange", onMaybeRotated, true);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("orientationchange", onMaybeRotated, true);
      window.removeEventListener("resize", onMaybeRotated, true);
      document.removeEventListener("visibilitychange", onMaybeRotated, true);
    };
  }, [isVideo, hardFailed, scene?.id, active]);

  // ── Outgoing half of a crossfade ─────────────────────────────────────
  // Never keep a live <video> mounted while fading out (iOS decoder slots).
  // Preferred: the EXACT frame the user was seeing, captured into a canvas
  // by PublicViewer at transition start. Fallback: poster (only correct when
  // the video never revealed, i.e. the poster WAS what was on screen).
  if (fadingOut) {
    if (frozenFrame) {
      return <FrozenFrame canvas={frozenFrame} className={cls} style={outgoingStyle} fit={effectiveFit} />;
    }
    if (isVideo) {
      if (scene.poster_url) {
        return (
          <img
            src={scene.poster_url}
            alt=""
            className={cls}
            style={outgoingStyle}
            draggable={false}
            data-testid="scene-media-frozen"
          />
        );
      }
      // No frame, no poster — render nothing; the incoming scene is already
      // fully opaque underneath, so nothing flashes.
      return null;
    }
    return (
      <img src={scene.media_url} alt="" className={cls} style={outgoingStyle} draggable={false} data-testid="scene-media-frozen" />
    );
  }

  // Video path → only swap to the static poster on DEFINITIVE failure.
  if (isVideo && !hardFailed) {
    return (
      <>
        {scene.poster_url && (
          <img
            src={scene.poster_url}
            alt=""
            className={cls}
            draggable={false}
            aria-hidden="true"
            data-testid="scene-media-poster-base"
          />
        )}
        <video
          ref={videoRef}
          src={scene.media_url}
          className={fitCls}
          style={{
            opacity: videoRevealed ? 1 : 0,
            visibility: videoRevealed ? "visible" : "hidden",
            // Resuming from an exact frame (or underneath an outgoing frame)
            // must be opaque immediately; fading would expose the poster.
            transition: videoRevealed && !capturedFrameRef.current && !skipFadeInRef.current ? "opacity 160ms ease" : "none",
          }}
          autoPlay={active}
          loop
          muted
          playsInline
          // eslint-disable-next-line react/no-unknown-property
          webkit-playsinline="true"
          // eslint-disable-next-line react/no-unknown-property
          x-webkit-airplay="allow"
          disableRemotePlayback
          preload={active ? "auto" : "none"}
          poster={scene.poster_url || undefined}
          onPlaying={(event) => {
            if (!activeRef.current || disposedRef.current) {
              event.currentTarget.pause();
              return;
            }
            if (event.currentTarget.paused) {
              holdPausedFrame(event.currentTarget);
              return;
            }
            if (revealed) { onReady?.(); return; }
            cancelVideoPresentation();
            const video = event.currentTarget;
            const presentation = videoPresentationRef.current;
            cancelVideoFrameRef.current = afterVideoFrame(video, () => {
              if (disposedRef.current || !activeRef.current || videoPresentationRef.current !== presentation || videoRef.current !== video || !video.isConnected || video.paused || video.readyState < 2) return;
              decodedFrameDisplayedRef.current = true;
              setRevealed(true);
            });
            onReady?.();
          }}
          onPause={(event) => {
            if (!disposedRef.current && activeRef.current) holdPausedFrame(event.currentTarget);
          }}
          onCanPlay={(e) => {
            if (activeRef.current) { presentDecodedFrame(e.currentTarget); onReady?.(); }
          }}
          onLoadedMetadata={(e) => reportDims(e.currentTarget)}
          onLoadedData={(e) => { reportDims(e.currentTarget); presentDecodedFrame(e.currentTarget); }}
          onError={(e) => {
            // Source, decoder and network errors cannot present this media.
            // ABORTED (1) can follow navigation — let the browser recover.
            const code = e.currentTarget.error?.code;
            const message = e.currentTarget.error?.message || "";
            // eslint-disable-next-line no-console
            console.warn(`[scene-media] video error code=${code} message=${message}`);
            if (code === 4 || code === 3 || code === 2) {
              cancelVideoPresentation();
              cancelCanvasPaintRef.current?.();
              setHardFailed(true);
              if (!scene.poster_url) onDisplayFailed?.();
            }
          }}
          data-testid="scene-media-video"
        />
        {pausedFrame && (
          <FrozenFrame canvas={pausedFrame.canvas} className={fitCls} fit={effectiveFit} />
        )}
      </>
    );
  }
  if (isVideo && hardFailed) {
    if (scene.poster_url) {
      return (
        <img
          src={scene.poster_url}
          alt=""
          className={cls}
          draggable={false}
          data-testid="scene-media-fallback-poster"
          onLoad={(e) => displayLoadedImage(e.currentTarget)}
          onError={() => onDisplayFailed?.()}
        />
      );
    }
    return <div className={cls} data-testid="scene-media-empty" />;
  }
  return (
    <img
      src={scene.media_url}
      alt=""
      className={cls}
      draggable={false}
      onLoad={(e) => displayLoadedImage(e.currentTarget)}
      onError={() => onDisplayFailed?.()}
      data-testid="scene-media-image"
    />
  );
}
